const siteUrl = "https://corticacare.com"

//Corporation Schema
export const getCorporationSchema = () => ({
  "@context": "https://schema.org",
  "@id": siteUrl,
  "@type": "Corporation",
  name: "Cortica",
  url: siteUrl,
  logo: `${siteUrl}/images/Cortica_logo.jpg`,
  description:
    "Cortica provides comprehensive autism testing and treatment for children.",
  address: {
    "@type": "PostalAddress",
    addressCountry: "US",
  },
})

//Local Business Schema
/**
 * Opening hours, read out of the clinic's Sidebar Details.
 *
 * That field is the only place the hours exist as data; the richer schema some
 * entries carry was pasted in by hand. Three wordings appear in the real
 * content: "Center Hours" and "Therapy Hours", and a couple of Arizona centres
 * that split the week ("M-Th ... Friday ..."). Anything after "Phone Operator"
 * is ignored, since those are call-centre hours, not opening hours.
 *
 * Checked against the 27 entries that carry hand-written hours: every one
 * matched. The Woodlands has an empty field and so gets none, which is better
 * than guessing.
 */
const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
]

const DAY_NAMES = {
  f: "Friday",
  fri: "Friday",
  friday: "Friday",
  m: "Monday",
  mon: "Monday",
  monday: "Monday",
  sat: "Saturday",
  saturday: "Saturday",
  sun: "Sunday",
  sunday: "Sunday",
  t: "Tuesday",
  th: "Thursday",
  thu: "Thursday",
  tu: "Tuesday",
  tue: "Tuesday",
  w: "Wednesday",
  wed: "Wednesday",
}

const expandDays = token => {
  const clean = token.trim().toLowerCase().replace(/\s/g, "")
  if (!clean.includes("-")) {
    return DAY_NAMES[clean] ? [DAY_NAMES[clean]] : null
  }

  const [from, to] = clean.split("-")
  const start = DAYS.indexOf(DAY_NAMES[from])
  const end = DAYS.indexOf(DAY_NAMES[to])
  if (start === -1 || end === -1 || start > end) return null

  return DAYS.slice(start, end + 1)
}

const to24Hour = (hour, minute, meridiem) => {
  let h = Number(hour)
  if (/p/i.test(meridiem) && h !== 12) h += 12
  if (/a/i.test(meridiem) && h === 12) h = 0
  return `${String(h).padStart(2, "0")}:${minute}`
}

const richText = node => {
  if (!node || typeof node !== "object") return ""
  if (node.nodeType === "text") return node.value || ""
  return (node.content || []).map(richText).join(" ")
}

export const getOpeningHours = sidebarDetails => {
  const all = richText(sidebarDetails?.json).replace(/\s+/g, " ")
  const centre = all.split(/Phone Operator/i)[0]
  const match = centre.match(/(?:Center|Therapy|Clinic)\s+Hours(.*)/i)
  if (!match) return undefined

  const ranges = match[1].matchAll(
    /([A-Za-z]{1,9}(?:\s*-\s*[A-Za-z]{1,9})?)\s*:\s*(\d{1,2}):(\d{2})\s*([ap])\.?m\.?\s*[-\u2013]\s*(\d{1,2}):(\d{2})\s*([ap])\.?m/gi
  )

  const hours = []
  for (const range of ranges) {
    const days = expandDays(range[1])
    if (!days) continue
    hours.push({
      "@type": "OpeningHoursSpecification",
      closes: to24Hour(range[5], range[6], range[7]),
      dayOfWeek: days,
      opens: to24Hour(range[2], range[3], range[4]),
    })
  }

  return hours.length ? hours : undefined
}

/**
 * The services every centre offers, as the hand-written entries word them.
 *
 * Listed in full here including the two not offered everywhere; the caller
 * trims those per centre, the same filter the page copy uses, so this list and
 * the visible copy cannot disagree.
 */
const SERVICES = [
  "Autism Diagnostic Evaluations",
  "Applied Behavior Analysis (ABA) Therapy",
  "Jumpstart Early Intervention Program",
  "Speech-Language Therapy",
  "Occupational Therapy",
  "Physical Therapy",
  "Music Therapy",
  "Medical Care",
  "Mental Health Counseling",
]

export const getLocalBusinessSchema = clinic => {
  // Extract city and state from clinic name (e.g., "Burlington, MA")
  const nameParts = clinic.name?.split(",") || []
  const city = nameParts[0]?.trim() || "Unknown"
  const state = nameParts[1]?.trim() || "Unknown"

  return {
    "@context": "https://schema.org",
    "@id": siteUrl,
    // MedicalClinic as well as LocalBusiness. Some entries carry a
    // hand-written graph that says both; this fallback said only the latter,
    // so any centre without one described itself as a generic business.
    "@type": ["MedicalClinic", "LocalBusiness"],
    availableService: SERVICES.map(name => ({
      "@type": "MedicalTherapy",
      name,
    })),
    medicalSpecialty: "Pediatrics",
    openingHoursSpecification: getOpeningHours(clinic.sidebarDetails),
    name: clinic.meta?.title || clinic.name,
    url: `${siteUrl}/locations/${clinic.slug}`,
    image: clinic.image?.file?.url
      ? `https:${clinic.image.file.url}`
      : undefined,
    telephone: clinic.phone,
    faxNumber: clinic.fax,
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: clinic.detailedAddress,
      addressLocality: city,
      addressRegion: state,
      postalCode: clinic.postalCode,
      addressCountry: "US",
    },
    geo:
      clinic.address?.lat && clinic.address?.lon
        ? {
            "@type": "GeoCoordinates",
            latitude: clinic.address.lat,
            longitude: clinic.address.lon,
          }
        : undefined,
  }
}

// Article Schema
export const getArticleSchema = careNote => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: careNote.meta?.title || careNote.title,
  description: careNote.meta?.description?.description,
  image: careNote.meta?.image?.file?.url
    ? `https:${careNote.meta.image.file.url}`
    : undefined,
  author: {
    "@type": "Organization",
    name: "Cortica",
    url: siteUrl,
  },
  publisher: {
    "@type": "Organization",
    name: "Cortica",
    url: siteUrl,
    logo: {
      "@type": "ImageObject",
      url: `${siteUrl}/images/Cortica_logo.jpg`,
    },
  },
  datePublished: careNote.createdAt,
  dateModified: careNote.updatedAt || careNote.createdAt,
})

// Fallback Schema function
export const getSchemaWithFallback = (contentfulJson, fallbackSchema) => {
  return contentfulJson || fallbackSchema
}
